import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  CreateCelulaPayload,
  CreateCelulaResponse,
  DeleteCelulaResponse,
  GetCelulasMapaResponse,
  GetCelulasResponse,
  UpdateCelulaPayload,
  UpdateCelulaResponse,
} from '../../shared/interfaces/celula.interface';
import { skipLoading } from '../interceptors/skip-loading.context';
import { UserStore } from './user-store.service';

const MAX_CELULAS_POR_LIDER = 3;

@Injectable({ providedIn: 'root' })
export class CelulasService {
  private readonly http = inject(HttpClient);
  private readonly userStore = inject(UserStore);
  private readonly apiUrl = environment.apiUrl;

  getByLeaderId(liderId: string): Observable<GetCelulasResponse> {
    return this.http
      .get<GetCelulasResponse>(`${this.apiUrl}/celulas`, {
        params: {
          liderId,
          limit: MAX_CELULAS_POR_LIDER,
        },
      })
      .pipe(catchError((error: HttpErrorResponse) => this.handleEmptyCelulas(error)));
  }

  getCurrentUserCelulas(): Observable<GetCelulasResponse> {
    const id = this.userStore.user()?._id;
    if (!id) {
      return throwError(() => new Error('No hay usuario en sesión'));
    }

    return this.getByLeaderId(id);
  }

  getMapa(iglesia?: string): Observable<GetCelulasMapaResponse> {
    if (!this.userStore.isAdmin() && !this.userStore.isSuper()) {
      return throwError(() => new HttpErrorResponse({ status: 403, statusText: 'Forbidden' }));
    }
    return this.http.get<GetCelulasMapaResponse>(`${this.apiUrl}/celulas/mapa`, {
      context: skipLoading(),
      params: iglesia ? { iglesia } : undefined,
    });
  }

  create(payload: CreateCelulaPayload): Observable<CreateCelulaResponse> {
    return this.http.post<CreateCelulaResponse>(`${this.apiUrl}/celulas`, payload, {
      context: skipLoading(),
    });
  }

  update(id: string, payload: UpdateCelulaPayload): Observable<UpdateCelulaResponse> {
    return this.http.put<UpdateCelulaResponse>(`${this.apiUrl}/celulas/${id}`, payload, {
      context: skipLoading(),
    });
  }

  delete(id: string): Observable<DeleteCelulaResponse> {
    return this.http.delete<DeleteCelulaResponse>(`${this.apiUrl}/celulas/${id}`, {
      context: skipLoading(),
    });
  }

  private handleEmptyCelulas(error: HttpErrorResponse): Observable<GetCelulasResponse> {
    if (error.status === 404) {
      return of({
        success: true,
        data: [],
        pagination: {
          page: 1,
          limit: MAX_CELULAS_POR_LIDER,
          total: 0,
        },
      });
    }

    return throwError(() => error);
  }
}
